// @flow
/* eslint-disable ta-code-conventions/has-matching-storydoc,ta-code-conventions/has-matching-test,ta-code-conventions/require-jsdoc */
import React, { Fragment, useContext, useMemo, memo } from 'react';
import classNames from '@ta/common.classnames';
import { Device } from '@ta/lithium.context';
import { useIntlFormatter } from '@ta/platform.i18n';
import {
  today,
  getFirstDayOfMonth,
  getLastDayOfMonth,
  getDifferenceMonths,
  useShortMonthLabels,
} from './dateUtils';
import styles from './MonthPicker.css';

type Props = {|
  maxRangeMonths?: number,

  /**
   * A date in the first month of the range, inclusive
   */
  startMonth: ?Date,

  /**
   * A date in the last month of the range, inclusive
   */
  endMonth: ?Date,

  onStartMonthChanged: (?Date) => mixed,

  onEndMonthChanged: (?Date) => mixed,
|};

/**
 * A date picker that supports selecting a flexible range of months without exact days
 */
const MonthPicker = ({
  maxRangeMonths = 4,
  startMonth,
  endMonth,
  onStartMonthChanged,
  onEndMonthChanged,
}: Props) => {
  const { viewportCategory } = useContext(Device);
  const intlFormatter = useIntlFormatter();
  const shortMonthLabels = useShortMonthLabels();

  // Some languages don't have month abbreviations like "OCT" or "DEC". In those languages, show
  // the months in a 3x8 grid on mobile instead of a 4x6 grid so they fit horizontally.
  let columnCount = 4;
  let rowCount = 6;
  if (viewportCategory === 'MOBILE' && shortMonthLabels.some(monthLabel => monthLabel.length > 5)) {
    columnCount = 3;
    rowCount = 8;
  }

  // Months to display in the picker, grouped by year
  const displayMonths: {| year: number, months: Date[] |}[] = useMemo(() => {
    const displayYears = [];

    new Array(columnCount * rowCount)
      .fill(0)
      .map((_, index) => index - (today.getMonth() % columnCount))
      .map(monthOffset => new Date(today.getFullYear(), today.getMonth() + monthOffset, 1))
      .forEach(month => {
        const displayYear = displayYears[displayYears.length - 1];

        if (displayYear == null || displayYear.year !== month.getFullYear()) {
          displayYears.push({
            year: month.getFullYear(),
            months: [month],
          });
        } else {
          displayYear.months.push(month);
        }
      });

    return displayYears;
  }, [columnCount, rowCount]);

  const firstDayOfStartMonth = startMonth && getFirstDayOfMonth(startMonth);
  const firstDayOfEndMonth = endMonth && getFirstDayOfMonth(endMonth);

  const monthClicked = (month: Date) => {
    // If there are no months, a range of months, or a far away month already selected, select only
    // the clicked month
    if (
      firstDayOfStartMonth == null ||
      firstDayOfEndMonth == null ||
      firstDayOfStartMonth.getTime() !== firstDayOfEndMonth.getTime() ||
      getDifferenceMonths(firstDayOfStartMonth, month) >= maxRangeMonths
    ) {
      onStartMonthChanged(getFirstDayOfMonth(month));
      onEndMonthChanged(getLastDayOfMonth(month));
    }
    // If the clicked date is already the only selected month, deselect it
    else if (firstDayOfStartMonth.getTime() === month.getTime()) {
      onStartMonthChanged(null);
      onEndMonthChanged(null);
    }
    // Otherwise, select a range of days from the existing selected month to the clicked month
    else if (firstDayOfStartMonth < month) {
      onEndMonthChanged(getLastDayOfMonth(month));
    } else {
      onStartMonthChanged(getFirstDayOfMonth(month));
    }
  };

  return (
    <div className={styles.monthPicker}>
      {displayMonths.map(({ year, months }) => (
        <Fragment key={year}>
          <div className={styles.year}>{year}</div>
          <div className={styles.monthsContainer}>
            {months.map(month => {
              const disabled = month < getFirstDayOfMonth(today);

              const selected =
                !disabled &&
                firstDayOfStartMonth != null &&
                firstDayOfEndMonth != null &&
                month >= firstDayOfStartMonth &&
                month <= firstDayOfEndMonth;

              return (
                <div
                  key={month.toISOString()}
                  className={classNames(styles.monthContainer, {
                    [styles.threeColumns]: columnCount === 3,
                    [styles.fourColumns]: columnCount === 4,
                  })}
                >
                  <button
                    className={classNames(styles.month, { [styles.selected]: selected })}
                    disabled={disabled}
                    onClick={() => monthClicked(month)}
                    aria-label={intlFormatter.formatDate('date_full_month_year', month)}
                    aria-pressed={selected}
                  >
                    {shortMonthLabels[month.getMonth()]}
                  </button>
                </div>
              );
            })}
          </div>
        </Fragment>
      ))}
    </div>
  );
};

export default memo<Props>(MonthPicker);
